import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Printer, ChevronDown, ArrowRight, Church, ExternalLink } from "lucide-react";
import Seo from "@/components/Seo";
import PageIntro from "@/components/PageIntro";
import { Reveal, EASE } from "@/components/landing/Reveal";
import { PRAYERS, PRAYER_CATEGORIES } from "@/data/prayers";
import { LitanyText } from "@/components/prayers/LitanyText";
import { PublishedPrayer } from "@/components/prayers/PublishedPrayer";
import { RosaryCatalogueGuide } from "@/components/prayers/RosaryCatalogueGuide";
import { APPARITION_PRAYER_SHRINES, PRAYER_AUTHENTICITY_LABELS } from "@/data/apparitionPrayers";
import { imageForPrayer } from "@/data/prayerImages";
import { openPrintCard } from "@/lib/printCard";
import { openPrintBooklet } from "@/lib/printBooklet";
import { BookOpen } from "lucide-react";
import { MarianQuote } from "@/components/MarianQuote";
import { PrayerAudio } from "@/components/prayers/PrayerAudio";
import { SUB_TUUM } from "@/data/historicalPrayers";
import { MonthlyDevotions } from "@/components/prayers/MonthlyDevotions";

const DEFAULT_PRAYER_BLESSING = SUB_TUUM.text;

const searchKey = (value) => String(value).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

export default function PrayerPage() {
  const location = useLocation();
  const requestedPrayer = PRAYERS.find((prayer) => `#${prayer.id}` === location.hash);
  const [cat, setCat] = useState(requestedPrayer?.cat || (location.hash === "#apparitions" ? "apparitions" : "core"));
  const [openId, setOpenId] = useState(requestedPrayer?.id || null);
  const [printError, setPrintError] = useState("");
  const [shrine, setShrine] = useState(requestedPrayer?.apparitionId || "all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (location.hash === "#apparitions") {
      setCat("apparitions"); setShrine("all"); setSearch(""); setOpenId(null);
      const frame = window.requestAnimationFrame(() => document.getElementById("prayer-library")?.scrollIntoView({ block: "start" }));
      return () => window.cancelAnimationFrame(frame);
    }
    const prayer = PRAYERS.find((item) => `#${item.id}` === location.hash);
    if (!prayer) return;
    setCat(prayer.cat); setOpenId(prayer.id); setShrine(prayer.apparitionId || "all"); setSearch("");
    const frame = window.requestAnimationFrame(() => document.getElementById(prayer.id)?.scrollIntoView({ block: "start" }));
    return () => window.cancelAnimationFrame(frame);
  }, [location.hash]);

  const filtered = useMemo(() => PRAYERS.filter((p) => (cat === "apparitions" ? Boolean(p.apparitionId) : p.cat === cat)
    && (cat !== "apparitions" || shrine === "all" || p.apparitionId === shrine)
    && searchKey(`${p.title} ${p.subtitle} ${p.apparitionTitle || ""} ${p.id}`).includes(searchKey(search.trim()))), [cat, shrine, search]);

  const printPrayer = (prayer, layout) => {
    setPrintError("");
    const card = {
      eyebrow: prayer.tag,
      title: prayer.displayTitle || prayer.title,
      subtitle: [prayer.subtitle, prayer.origin].filter(Boolean).join(" — "),
      image: imageForPrayer(prayer),
      blocks: (prayer.lines || []).map(([lang, text]) => ({
        label: lang, text,
        dir: lang === "Arabic" ? "rtl" : undefined,
        italic: ["Latin", "Greek"].includes(lang),
      })),
      note: prayer.sourceNote,
      source: prayer.sourceUrl ? { label: prayer.sourceLabel, url: prayer.sourceUrl } : undefined,
      back: {
        eyebrow: prayer.apparitionId ? "From the shrine of" : "Under her mantle",
        name: prayer.apparitionTitle || prayer.displayTitle || prayer.title,
        blessing: `${DEFAULT_PRAYER_BLESSING}\n\n${SUB_TUUM.sourceLabel}`,
      },
    };
    const error = openPrintCard(card, layout);
    if (error) setPrintError(error);
  };

  const printBooklet = () => {
    setPrintError("");
    const toItem = (p) => ({
      title: p.displayTitle || p.title,
      subtitle: [p.subtitle, p.origin].filter(Boolean).join(" — "),
      tag: p.tag,
      image: imageForPrayer(p),
      blocks: (p.lines || []).map(([lang, text]) => ({ label: lang, text, dir: lang === "Arabic" ? "rtl" : undefined, italic: ["Latin", "Greek"].includes(lang) })),
      note: p.sourceNote,
      source: p.sourceUrl ? { label: p.sourceLabel, url: p.sourceUrl } : undefined,
      guide: p.format === "rosary-guide",
    });
    const sections = PRAYER_CATEGORIES
      .map((c) => ({ heading: c.label, prayers: PRAYERS.filter((p) => p.cat === c.id).map(toItem) }))
      .filter((s) => s.prayers.length);
    const error = openPrintBooklet(sections, {
      eyebrow: "A home booklet of Marian prayer",
      title: "A Treasury of Marian Prayer",
      subtitle: "The prayers of East and West — to pray together as a family, in the language they were first sung and in English",
      coverImage: "/images/treasury/our-lady-rosary.webp",
      coverAlt: "Our Lady of the Rosary",
    });
    if (error) setPrintError(error);
  };

  return (
    <main data-testid="prayer-page">
      <Seo
        title="Prayer & Devotion — Marian prayers of East and West · Theotokos · Mater Dei"
        siteName="Theotokos · Mater Dei"
        description="A treasury of Marian prayers with cited historical English and original-language texts, named publisher editions, licensed chant recordings where available, and printable prayer cards."
      />
      <PageIntro
        crumbs={[{ label: "Home", to: "/" }, { label: "Prayer & Devotion" }]}
        eyebrow="Prayer & Devotion · East and West"
        lines={["A treasury", "of Marian prayer"]}
        aside={
          <Link
            to="/rosary"
            data-testid="prayer-to-rosary"
            className="group block rounded-2xl border border-marian/15 bg-white/70 p-5 transition-colors hover:border-gold"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">The great prayer of the Church</p>
            <p className="mt-2 font-serif text-2xl font-semibold text-midnight">Pray the Rosary →</p>
            <p className="mt-1 text-xs text-slateblue">Step-by-step with all four mysteries and every prayer in full.</p>
          </Link>
        }
      >
        The Church prays with two lungs. Here, side by side, are the prayers we say to the Mother of
        God — in the language they were first sung and in English — from the Ave Maria of the West
        to the Axion Estin of Athos, the Salaam ya Maryam of the Arab Catholic Churches, and the
        Sub Tuum of the desert Fathers.
      </PageIntro>

      <MonthlyDevotions prefix="prayer-treasury" />
      <MarianQuote
        testid="prayer-montfort-quote"
        text="This devotion is a smooth, short, perfect and sure way of attaining union with our Lord, in which Christian perfection consists."
        author="St. Louis de Montfort"
        source="True Devotion to the Blessed Virgin, §152 · Montfort Missionaries translation"
        url="https://www.montfort.org.uk/Writings/TD.php"
      />

      <section id="prayer-library" className="mx-auto max-w-7xl scroll-mt-28 px-4 py-12 sm:px-8 lg:px-16">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="max-w-xl text-sm leading-relaxed text-charcoal/80">Every prayer can be printed as a picture holy card. You can also print the whole treasury as one home booklet.</p>
          <button
            type="button"
            onClick={printBooklet}
            data-testid="print-family-booklet"
            className="group inline-flex shrink-0 items-center gap-2 self-start rounded-full bg-marian px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-midnight sm:self-auto"
          >
            <BookOpen className="h-4 w-4" />
            Print the family booklet
          </button>
        </div>
        <div role="tablist" aria-label="Prayer category" className="flex flex-wrap gap-2 border-b border-marian/15 pb-1" data-testid="prayer-tabs">
          {PRAYER_CATEGORIES.map((c) => (
            <button
              key={c.id}
              role="tab"
              aria-selected={cat === c.id}
              onClick={() => { setCat(c.id); setOpenId(null); setShrine("all"); setSearch(""); setPrintError(""); }}
              data-testid={`prayer-tab-${c.id}`}
              className={`relative rounded-full px-4 py-2 text-sm font-semibold transition-colors ${cat === c.id ? "text-midnight" : "text-slateblue hover:text-marian"}`}
            >
              {cat === c.id && (
                <motion.span layoutId="prayer-tab-pill" className="absolute inset-0 rounded-full bg-paleblue/80" transition={{ duration: 0.35, ease: EASE }} />
              )}
              <span className="relative">{c.label}</span>
            </button>
          ))}
        </div>

        {(cat === "apparitions" || cat === "fatima") && <div className="mt-6 border-b border-marian/15 pb-6" data-testid="shrine-prayer-filters">
          <p className="mb-5 max-w-3xl text-xs leading-relaxed text-slateblue" data-testid="prayer-provenance-note">Historical prayers, reported prayers and supplied papal texts are identified separately. Full publisher editions are linked where they are not reproduced here. A shrine-published prayer is not automatically an apparition quotation.</p>
          <div className="grid gap-4 sm:grid-cols-2">
            {cat === "apparitions" && <label className="flex min-w-0 flex-col gap-2 text-xs font-semibold text-marian">Shrine<select value={shrine} onChange={(e) => { setShrine(e.target.value); setOpenId(null); }} data-testid="prayer-shrine-filter" className="min-w-0 w-full rounded-lg border border-marian/25 bg-white p-3 text-sm text-midnight"><option value="all">{`All ${APPARITION_PRAYER_SHRINES.length} shrines and devotions`}</option>{APPARITION_PRAYER_SHRINES.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}</select></label>}
            <label className="flex min-w-0 flex-col gap-2 text-xs font-semibold text-marian">Find a prayer<input value={search} onChange={(e) => setSearch(e.target.value)} type="search" data-testid="prayer-library-search" placeholder="Prayer or shrine name" className="min-w-0 rounded-lg border border-marian/25 bg-white p-3 text-sm text-midnight" /></label>
          </div>
          <p className="mt-3 text-xs text-slateblue" data-testid="prayer-filter-count">{filtered.length} prayers and publisher editions</p>
        </div>}
        {filtered.length === 0 && <p className="mt-8 text-sm text-slateblue" data-testid="prayer-library-empty">No prayers match this selection.</p>}

        <ul className="mt-8 space-y-4" data-testid="prayer-list">
          {filtered.map((p, i) => {
            const open = openId === p.id;
            return (
              <Reveal key={p.id} delay={i * 0.03}>
                <li
                  data-testid={`prayer-${p.id}`}
                  data-prayer-id={p.id}
                  data-authenticity={p.authenticity}
                  id={p.id}
                  className="scroll-mt-28 overflow-hidden rounded-3xl border border-marian/15 bg-white/95 shadow-[0_18px_40px_-30px_rgba(16,42,67,0.35)]"
                >
                  <button
                    type="button"
                    data-testid={`prayer-toggle-${p.id}`}
                    onClick={() => setOpenId(open ? null : p.id)}
                    aria-expanded={open}
                    className="flex w-full items-start gap-4 px-5 py-4 text-left transition-colors hover:bg-paleblue/30 sm:px-7 sm:py-5"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">{p.tag}</p>
                      <p className="mt-1 font-serif text-2xl font-semibold text-midnight">{p.title}</p>
                      <p className="mt-0.5 font-serif text-sm italic text-slateblue">{p.subtitle}</p>
                      {p.apparitionTitle && <p className="mt-2 text-xs font-semibold text-marian" data-testid={`prayer-shrine-${p.id}`}>{p.apparitionTitle} · {p.authenticityLabel || PRAYER_AUTHENTICITY_LABELS[p.authenticity]}</p>}
                    </div>
                    <span className={`mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-marian/25 text-marian transition-transform duration-300 ${open ? "rotate-180 bg-marian text-ivory" : ""}`} aria-hidden="true">
                      <ChevronDown className="h-4 w-4" />
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div
                        key="body"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: EASE }}
                        className="overflow-hidden"
                      >
                        <div className="border-t border-marian/10 px-5 py-6 sm:px-7 sm:py-8">
                          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-slateblue">{p.origin}</p>
                          {p.format !== "rosary-guide" && (() => {
                            const art = imageForPrayer(p);
                            return (
                              <figure className="mt-6 overflow-hidden rounded-2xl border border-marian/15 bg-gradient-to-b from-paleblue/30 to-ivory" data-testid={`prayer-image-${p.id}`}>
                                <div className="flex items-center justify-center p-3 sm:p-4">
                                  <img src={art.src} alt={art.alt} loading="lazy" className="max-h-80 w-auto max-w-full rounded-lg object-contain shadow-[0_16px_40px_-28px_rgba(16,42,67,0.6)]" />
                                </div>
                                <figcaption className="border-t border-marian/10 px-4 py-2 text-[10px] leading-relaxed text-slateblue">{art.credit}</figcaption>
                              </figure>
                            );
                          })()}
                          <div className={`mt-6 grid gap-6 ${p.lines.length > 1 ? "md:grid-cols-2" : ""}`} data-testid={`prayer-body-${p.id}`}>
                            {p.format === "rosary-guide" ? <RosaryCatalogueGuide /> : p.externalOnly ? <PublishedPrayer prayer={p} prefix={`library-${p.id}`} /> : p.format === "litany" ? <LitanyText /> : p.lines.map(([lang, text]) => (
                              <div key={lang} className="rounded-2xl border border-marian/10 bg-paleblue/25 p-5">
                                <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">{lang}</p>
                                <p className={`mt-3 whitespace-pre-line font-serif leading-relaxed text-midnight ${lang === "Arabic" ? "text-right text-xl" : "text-base sm:text-lg"} ${["Latin", "Greek"].includes(lang) ? "italic" : ""}`} dir={lang === "Arabic" ? "rtl" : "ltr"}>
                                  {text}
                                </p>
                              </div>
                            ))}
                          </div>
                          <PrayerAudio prayerId={p.audioId || p.id} prayerTitle={p.title} prefix={`library-${p.id}`} lang={p.lines?.some(([language]) => language === "English") ? "en" : undefined} />
                          {p.sourceNote && <p className="mt-5 text-xs leading-relaxed text-slateblue" data-testid={`prayer-source-note-${p.id}`}>{p.sourceNote}</p>}
                          <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
                            {!p.externalOnly && p.sourceUrl && <a href={p.sourceUrl} target="_blank" rel="noopener noreferrer" data-testid={`prayer-source-${p.id}`} className="inline-flex items-center gap-2 text-xs font-semibold text-marian underline decoration-marian/30 underline-offset-4 transition-colors hover:text-gold">{p.sourceLabel}<ExternalLink size={12} aria-hidden="true" /></a>}
                            {!p.externalOnly && p.format !== "rosary-guide" && <div className="inline-flex overflow-hidden rounded-full border border-marian/25" data-testid={`prayer-print-group-${p.id}`}>
                              <button
                                type="button"
                                onClick={() => printPrayer(p, "card")}
                                data-testid={`prayer-print-${p.id}`}
                                className="group inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold text-marian transition-colors hover:bg-paleblue/50 hover:text-midnight"
                              >
                                <Printer className="h-3.5 w-3.5" />
                                Prayer card
                              </button>
                              <button
                                type="button"
                                onClick={() => printPrayer(p, "page")}
                                data-testid={`prayer-print-page-${p.id}`}
                                className="inline-flex items-center gap-2 border-l border-marian/25 px-4 py-2 text-xs font-semibold text-marian transition-colors hover:bg-paleblue/50 hover:text-midnight"
                              >
                                Full page
                              </button>
                            </div>}
                            {p.apparitionId && <Link to={`/apparitions/${p.apparitionId}`} data-testid={`prayer-back-to-shrine-${p.id}`} className="inline-flex items-center gap-2 text-xs font-semibold text-marian transition-colors hover:text-gold">Visit the shrine page<ArrowRight size={12} /></Link>}
                          </div>
                          {printError && <p role="alert" data-testid="prayer-print-error" className="mt-3 text-sm text-red-800">{printError}</p>}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </li>
              </Reveal>
            );
          })}
        </ul>

        <Reveal>
          <div className="mt-14 grid gap-4 rounded-3xl bg-midnight px-6 py-10 text-ivory sm:grid-cols-[1fr_auto] sm:items-center sm:px-10">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">A whole life of prayer with Mary</p>
              <p className="mt-2 font-serif text-2xl sm:text-3xl">The Marian year of feasts and the mysteries of the Rosary complete this treasury.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/rosary" data-testid="cta-rosary" className="group inline-flex items-center gap-2 rounded-full bg-gold px-5 py-3 text-sm font-semibold text-midnight transition-colors hover:bg-ivory">
                Pray the Rosary
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/calendar" data-testid="cta-calendar" className="group inline-flex items-center gap-2 rounded-full border border-ivory/30 px-5 py-3 text-sm font-semibold text-ivory transition-colors hover:border-gold hover:text-gold">
                <Church className="h-4 w-4" />
                Marian calendar
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
